import { IVideoTracking } from '@/document-tracking/types/video-tracking';
import { IPageTracking } from '@/document-tracking/types/page-tracking';
import { EDocumentPageType } from '@/enums/document-page-type';
import { IPageTime } from '@/document-view/types/page-time';
import { IClickTracking } from '@/document-tracking/types/click-tracking';
import { IDocumentPage } from '@/types/document-page';
import { IContentLinkBlock } from '@/dealroom/content-link/types/content-link';

export function getPageTimes(
  clickTrackings: IClickTracking[],
  videoTrackings: IVideoTracking[],
  pageTrackings: IPageTracking[],
  recipientId?: string,
  documentPages?: IDocumentPage[]
): IPageTime[] {
  const pageNumToPageTime: Map<number, number> = getPageTimeByPage(
    documentPages,
    clickTrackings,
    videoTrackings,
    pageTrackings,
    recipientId
  );
  return documentPages.map((page: IDocumentPage) => ({
    page_id: page.id,
    page_num: page.pageNum,
    page_type: page.pageType,
    page_time: pageNumToPageTime.get(page.pageNum),
    page_click:
      page.pageType === EDocumentPageType.ContentLink
        ? getContentLinkClicks(page.contentLinkBlock.id, clickTrackings, recipientId)
        : null,
    contentLinkBlock:
      page.pageType === EDocumentPageType.ContentLink
        ? getContentLinkData(page.id, documentPages)
        : null,
  }));
}

export function getPageTimeByPage(
  pages: IDocumentPage[],
  clickTrackings: IClickTracking[],
  videoTrackings: IVideoTracking[],
  pageTrackings: IPageTracking[],
  recipientId?: string
): Map<number, number> {
  const pageNumToPageTime: Map<number, number> = new Map();

  for (const page of pages) {
    switch (page.pageType) {
      case EDocumentPageType.DocumentContent:
      case EDocumentPageType.ContentLink:
      case EDocumentPageType.Editor:
        pageNumToPageTime.set(page.pageNum, getPageTime(page.pageNum, pageTrackings, recipientId));
        break;
      case EDocumentPageType.Video:
        pageNumToPageTime.set(
          page.pageNum,
          getVideoTime(page.video.id, videoTrackings, recipientId)
        );
        break;
    }
  }
  return pageNumToPageTime;
}

export function getPageTime(
  pageNum: number,
  pageTrackings: IPageTracking[],
  recipientId?: string
): number {
  const filteredPageTrackings: IPageTracking[] = recipientId
    ? pageTrackings.filter((trackPage: IPageTracking) => trackPage.recipientId === recipientId)
    : pageTrackings;

  return filteredPageTrackings
    .filter((trackPage: IPageTracking) => trackPage.pageNum === pageNum)
    .map((trackPage: IPageTracking) => trackPage.pageTime)
    .reduce((a: number, b: number) => a + b, 0);
}

export function getVideoTime(
  videoId: string,
  videoTrackings: IVideoTracking[],
  recipientId?: string
): number {
  const filteredVideoTrackings: IVideoTracking[] = recipientId
    ? videoTrackings.filter((trackVideo: IVideoTracking) => trackVideo.recipientId === recipientId)
    : videoTrackings;

  return filteredVideoTrackings
    .filter((videoTrack: IVideoTracking) => videoTrack.videoId === videoId)
    .map((videoTrack: IVideoTracking) => videoTrack.viewDuration)
    .reduce((a: number, b: number) => a + b, 0);
}

function getContentLinkData(
  id: string,
  documentPages: IDocumentPage[]
): Partial<IContentLinkBlock> {
  const page = documentPages.find((page: IDocumentPage) => page.id === id);
  return { thumbUrl: page.contentLinkBlock.thumbUrl, id };
}

function getContentLinkClicks(
  contentLinkBlockId: string,
  clickTrackings: IClickTracking[],
  recipientId?: string
): number {
  const filteredClickTrackings: IClickTracking[] = recipientId
    ? clickTrackings.filter((trackClick: IClickTracking) => trackClick.recipientId === recipientId)
    : clickTrackings;

  return filteredClickTrackings.filter(
    (trackClick: IClickTracking) => trackClick.contentLinkBlockId === contentLinkBlockId
  ).length;
}

export function getUpdatedPageTrackings(
  existingTrackings: IPageTracking[],
  trackPageUpdate: IPageTracking
): IPageTracking[] {
  const updatedPageTrackings: IPageTracking[] = [...existingTrackings];
  const trackPageIndex: number = updatedPageTrackings.findIndex(
    ({ id }: IPageTracking) => id === trackPageUpdate.id
  );

  if (trackPageIndex >= 0) {
    updatedPageTrackings[trackPageIndex] = trackPageUpdate;
  } else {
    updatedPageTrackings.push(trackPageUpdate);
  }
  return updatedPageTrackings;
}
